/*
 * The treatment category chip row's data, owned in one place.
 *
 * Two surfaces offer this row - the search results page and the Full Product Category
 * listing section - and both have to agree with each other about which categories are worth
 * a chip, because both also have to CLEAR an applied category when its chip goes away. The
 * two disagreeing is exactly how a filter ends up applied with no chip to clear it.
 */

/**
 * The treatment categories present in a result set, with a count each.
 *
 * Derived from the results rather than fetched, which is V1's own rule
 * (TreatmentController::getCategories): a category with nothing behind it at this location
 * never gets a chip.
 *
 * Ordered by the admin's own drag order (TreatmentCategoryResource), which arrives as
 * `category_order` on each result. This is the front-end half of that feature: the reorder
 * saves and looks right in the admin whatever this file does, so sorting on anything else
 * here is a silent no-op for the customer. Name is the tiebreak, which is also the whole
 * ordering for a database predating the order column, where every row still reads 0.
 *
 * A result whose category_id points at a category row that no longer exists has no name to
 * show, so it is skipped here and counts only towards "All Treatments". The ported catalogue
 * carries no foreign keys, so that state is reachable.
 *
 * Selected on carrying the category keys rather than on `type === 'treatment'`. Identical
 * today, since no other payload carries them, and it is what lets the treatment-category
 * packages merged into the treatments listing join these counts without a second edit here
 * (docs/product-category-pages.md, phase 5).
 */
export function treatmentCategoriesIn(results) {
    const byId = new Map();

    results
        .filter(result => result.category_id && result.category_name)
        .forEach((result) => {
            const seen = byId.get(result.category_id);

            seen
                ? seen.count++
                : byId.set(result.category_id, {
                    id: result.category_id,
                    name: result.category_name,
                    // Optional, unlike the name: a category with no icon chosen yet renders
                    // a chip without one rather than being dropped.
                    icon: result.category_icon ?? null,
                    order: result.category_order ?? 0,
                    count: 1,
                });
        });

    return [...byId.values()]
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}

/**
 * One category is not a choice, so the row is not worth showing for it - an empty list here
 * means "no row", and every caller reads it as such.
 */
export function offerableCategories(categories) {
    return categories.length > 1 ? categories : [];
}
